import { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import moment from "moment";
import { getEmpresas, getSubproductosEntidad, getSubproductosBloqueados } from "../../../services/SubproductoService.js";
import { ProductService } from "../../../services/ProductService.js";
import { esMetrajeValido } from "../../product/utils/productoValidations.js";
import { esIncluido } from "../../../utilities/valorEsIncluido.js";

const productService = new ProductService();

const dateFormats = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "YYYY/MM/DD",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD MMM YYYY",
  "MMM DD, YYYY",
  "MMMM DD, YYYY",
  "YYYY MMM DD",
];

const FormAgregarSubproductoCotizacion = ({
  visible,
  onHide,
  products,
  baseUrl,
  idCotizacion,
  desde,
  hasta,
  detallesExistentes,
  loading,
  onGuardar,
}) => {
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [subproductoSeleccionado, setSubproductoSeleccionado] = useState(null);
  const [subproductos, setSubproductos] = useState([]);
  const [loadingSubproductos, setLoadingSubproductos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const guardandoRef = useRef(false);
  const [idsBloqueados, setIdsBloqueados] = useState(new Set());
  const [loadingBloqueados, setLoadingBloqueados] = useState(false);

  const idProductoSeleccionado = productoSeleccionado?.id_elemento ?? null;

  const limpiarYCerrar = () => {
    setProductoSeleccionado(null);
    setSubproductoSeleccionado(null);
    setSubproductos([]);
    setIdsBloqueados(new Set());
    setLoadingBloqueados(false);
    onHide?.();
  };

  const cargarSubproductos = async () => {
    try {
      setLoadingSubproductos(true);
      const nombresEmpresas = {};
      try {
        const empresas = await getEmpresas(baseUrl);
        (empresas || []).forEach((e) => {
          nombresEmpresas[e.id] = e.nombre;
        });
      } catch (e) {
        console.error("Error al obtener empresas:", e);
      }
      const data = await getSubproductosEntidad(baseUrl);
      const lista = Array.isArray(data) ? data : [];
      setSubproductos(
        lista
          .filter((sub) => sub.activo === true || sub.activo === 1)
          .map((sub) => ({
            ...sub,
            label:
              sub.descripcion +
              (sub.id_empresa && nombresEmpresas[sub.id_empresa]
                ? ` (${nombresEmpresas[sub.id_empresa]})`
                : ""),
          }))
      );
    } catch (error) {
      console.error("Error al obtener subproductos:", error);
      alert(error.message || "Error al obtener los subproductos");
      setSubproductos([]);
    } finally {
      setLoadingSubproductos(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    setSubproductoSeleccionado(null);
    setSubproductos([]);
    if (Array.isArray(products) && products.length === 1) {
      setProductoSeleccionado(products[0]);
    } else {
      setProductoSeleccionado(null);
    }
    cargarSubproductos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible || idProductoSeleccionado == null) {
      setIdsBloqueados(new Set());
      setLoadingBloqueados(false);
      return;
    }
    let vigente = true;
    setIdsBloqueados(new Set());
    setLoadingBloqueados(true);
    getSubproductosBloqueados(baseUrl, idProductoSeleccionado)
      .then((ids) => {
        if (vigente) setIdsBloqueados(new Set(ids));
      })
      .catch((error) => {
        console.error("Error al obtener subproductos bloqueados:", error);
        if (vigente) setIdsBloqueados(new Set());
      })
      .finally(() => {
        if (vigente) setLoadingBloqueados(false);
      });
    return () => {
      vigente = false;
    };
  }, [visible, baseUrl, idProductoSeleccionado]);

  const subproductosDisponibles = subproductos.filter(
    (sub) => !idsBloqueados.has(sub.id)
  );

  useEffect(() => {
    if (
      subproductoSeleccionado &&
      !subproductosDisponibles.some(
        (sub) => sub.id === subproductoSeleccionado.id
      )
    ) {
      setSubproductoSeleccionado(null);
    }
  }, [subproductosDisponibles, subproductoSeleccionado]);

  const handleProductoChange = (e) => {
    setProductoSeleccionado(e.value);
    setSubproductoSeleccionado(null);
  };

  const formatDateTramo = (dateInput) => {
    const date = moment(dateInput, dateFormats, true);
    return date.isValid() ? date.format("YYYY-MM-DD") : null;
  };

  const validar = () => {
    if (!productoSeleccionado || !subproductoSeleccionado) return false;

    if (!esMetrajeValido(subproductoSeleccionado.metraje)) {
      alert("El metraje del subproducto debe ser un número mayor a 0.");
      return false;
    }

    if (
      subproductoSeleccionado.tarifa_base == null ||
      subproductoSeleccionado.moneda == null
    ) {
      alert(
        "El subproducto no tiene una tarifa activa asociada. Debe configurar la tarifa del subproducto antes de agregarlo."
      );
      return false;
    }

    const descripcionEsperada = `${subproductoSeleccionado.descripcion} - ${productoSeleccionado.descripcion}`;
    const duplicado = (detallesExistentes || []).some(
      (item) => item.descripcion === descripcionEsperada
    );
    if (duplicado) {
      alert(
        `El producto "${productoSeleccionado.descripcion}" ya tiene el subproducto "${subproductoSeleccionado.descripcion}" agregado en la cotización.`
      );
      return false;
    }

    return true;
  };

  const construirNuevoSubproducto = () => {
    const incluido = esIncluido(subproductoSeleccionado.incluido);
    return {
      ...subproductoSeleccionado,
      descripcion: `${subproductoSeleccionado.descripcion} - ${productoSeleccionado.descripcion}`,
      tarifa_cliente: subproductoSeleccionado.tarifa_base,
      moneda: subproductoSeleccionado.moneda,
      relacion: "subproducto",
      desde: formatDateTramo(desde),
      hasta: formatDateTramo(hasta),
      descuento: 0,
      tramo: null,
      tipo: incluido ? "Incluido" : "Adicional",
      incluido,
      cantidad: Number(subproductoSeleccionado.metraje),
      cobro: "Factura Afecta",
      contrato: true,
      consolidado: false,
      agrupamiento: 0,
      id_elemento: subproductoSeleccionado.id,
      id_elemento_producto: productoSeleccionado.id_elemento,
      id: null,
      id_cotizacion: idCotizacion,
      metraje: Number(subproductoSeleccionado.metraje),
      codigo_producto: productoSeleccionado.codigo_producto,
    };
  };

  const handleGuardar = async () => {
    if (guardandoRef.current) return;
    if (!validar()) return;
    guardandoRef.current = true;
    setGuardando(true);
    try {
      try {
        await productService.insertProductoSubproductoEntidad(baseUrl, {
          idEmpresa: productoSeleccionado.id_empresa,
          idProducto: productoSeleccionado.id_elemento,
          idSubproducto: subproductoSeleccionado.id,
        });
      } catch (error) {
        console.error("Error al asociar el subproducto al producto:", error);
        alert(error.message || "Error al asociar el subproducto al producto");
        return;
      }
      await onGuardar?.(construirNuevoSubproducto(), productoSeleccionado);
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  return (
    <Dialog
      header="Asociar Subproducto 2"
      visible={visible}
      modal
      className="p-fluid product-dialog"
      onHide={limpiarYCerrar}
      footer={
        <div className="flex justify-content-end">
          <Button
            label="Cancelar"
            icon="pi pi-times"
            className="p-button-rounded p-button-outlined"
            onClick={limpiarYCerrar}
          />
          <Button
            label="Guardar"
            icon={guardando ? "pi pi-spin pi-spinner" : "pi pi-save"}
            className="p-button-rounded p-button-success p-button"
            raised
            onClick={handleGuardar}
            disabled={
              guardando ||
              !productoSeleccionado ||
              !subproductoSeleccionado ||
              loading ||
              loadingSubproductos
            }
          />
        </div>
      }
    >
      <div className="p-grid p-formgrid p-fluid">
        <div className="col-12 mb-2">

          <span className="p-float-label mt-2 mb-3">
            <Dropdown
              value={productoSeleccionado}
              options={products || []}
              onChange={handleProductoChange}
              optionLabel="descripcion"
              placeholder="Seleccione un producto"
              className="w-full"
            />
            <label htmlFor="productoSub">Producto</label>
          </span>

          {productoSeleccionado && (
            <>

              <div
                style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
              >
                <span className="p-float-label" style={{ width: "100%" }}>
                  <Dropdown
                    id="tipoSubP"
                    value={subproductoSeleccionado}
                    onChange={(e) => setSubproductoSeleccionado(e.value)}
                    options={subproductosDisponibles}
                    optionLabel="label"
                    placeholder="Seleccione un subproducto"
                    className="w-full"
                    loading={loadingSubproductos || loadingBloqueados}
                    emptyMessage="No hay subproductos disponibles"
                  />
                  <label htmlFor="tipoSubP">Subproducto</label>
                </span>
                {subproductoSeleccionado && (
                  <>
                    <span className="text-sm text-gray-600">
                      Tipo:{" "}
                      {subproductoSeleccionado?.tipo} - {esIncluido(subproductoSeleccionado?.incluido)
                        ? "Incluido"
                        : "Adicional"}
                    </span>

                    <span className="text-sm text-gray-600">
                      Metraje: {subproductoSeleccionado?.metraje ?? "-"} m²
                    </span>

                    <span className="text-sm text-gray-600">
                      Tarifa: {subproductoSeleccionado?.tarifa_base ?? "-"}{" "}
                      {subproductoSeleccionado?.moneda ?? ""}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
  
      </div>
    </Dialog>
  );
};

export default FormAgregarSubproductoCotizacion;
